import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logoutUser } from "../../store/slices/authSlice";
import { hasFeatureAccess, FEATURES } from "../../config/roleFeatures";
import ShortLogo from "../../assets/short-logo.svg";
import Logo from "../../assets/logo.svg";
import "../../style/sidebar.css";

import DashboardIcon from "../../assets/dashboard-icon.svg";
import DashboardIconActive from "../../assets/dashboard-icon-active.svg";
import ProductsIcon from "../../assets/products-icon.svg";
import PosIcon from "../../assets/pos-icon.svg";
import FinanceIcon from "../../assets/finance-icon.svg";
import VendorsIcon from "../../assets/vendors-icon.svg";
import CustomersIcon from "../../assets/customers-icon.svg";
import OrdersIcon from "../../assets/order-icon.svg";
import OrdersIconActive from "../../assets/order-icon-active.svg";

import ActiveProductsIcon from "../../assets/active-products-icon.svg";
import ActivePosIcon from "../../assets/active-pos-icon.svg";
import ActiveFinanceIcon from "../../assets/active-finance-icon.svg";
import ActiveVendorsIcon from "../../assets/active-vendors-icon.svg";
import ActiveCustomersIcon from "../../assets/active-customers-icon.svg";
import SalesIcon from "../../assets/sales-icon.svg";
import ActiveSalesIcon from "../../assets/active-sales.svg";
import EmpManagementIcon from "../../assets/emp-management-icon.svg";
import ActiveEmpManagementIcon from "../../assets/active-emp-management-icon.svg";

const Sidebar = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onCollapseChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const getUserInitials = (user) => {
    if (!user) return "";
    if (user.name) {
      const nameParts = user.name.trim().split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
      }
      return user.name.charAt(0).toUpperCase();
    }
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user.display_email) return user.display_email.charAt(0).toUpperCase();
    return "";
  };

  const getDisplayName = (user) => {
    if (!user) return "";
    if (user.name) return user.name;
    if (user.first_name && user.last_name)
      return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    if (user.display_email) return user.display_email.split("@")[0];
    return "";
  };

  const hasProfileImage = (user) => {
    return user && (user.profile_image || user.avatar || user.image);
  };

  const getProfileImageUrl = (user) => {
    if (!user) return null;
    return user.profile_image || user.avatar || user.image || null;
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapseChange) onCollapseChange(newCollapsedState);
  };

  const allMenuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: DashboardIcon,
      activeIcon: DashboardIconActive,
      feature: FEATURES.DASHBOARD,
    },
    { 
      id: "pos", 
      label: "POS", 
      icon: PosIcon, 
      activeIcon: ActivePosIcon,
      feature: FEATURES.POS,
    },
    { 
      id: "orders", 
      label: "Orders", 
      icon: OrdersIcon, 
      activeIcon: OrdersIconActive,
      feature: FEATURES.ORDERS,
    },
    {
      id: "sales",
      label: "Sales",
      icon: SalesIcon,
      activeIcon: ActiveSalesIcon,
      feature: FEATURES.SALES,
    },
    {
      id: "finance",
      label: "Finance",
      icon: FinanceIcon,
      activeIcon: ActiveFinanceIcon,
      feature: FEATURES.FINANCE,
    },
    {
      id: "products",
      label: "Products",
      icon: ProductsIcon,
      activeIcon: ActiveProductsIcon,
      feature: FEATURES.PRODUCTS,
    },
    {
      id: "customers",
      label: "Customers",
      icon: CustomersIcon,
      activeIcon: ActiveCustomersIcon,
      feature: FEATURES.CUSTOMERS,
    },
    {
      id: "vendors",
      label: "Suppliers",
      icon: VendorsIcon,
      activeIcon: ActiveVendorsIcon,
      feature: FEATURES.VENDORS,
    },
    {
      id: "imei",
      label: "IMEI Log",
      icon: require("../../assets/imei-barcode-icon.svg"),
      activeIcon: require("../../assets/active-imei-barcode-icon.svg"),
      feature: FEATURES.IMEI,
    },
    {
      id: "returns",
      label: "Returns",
      icon: require("../../assets/return-page-icon.svg"),
      activeIcon: require("../../assets/return-page-active-icon.svg"),
      feature: FEATURES.RETURNS,
    },
    {
      id: "employee-management",
      label: "Emp. Management",
      icon: EmpManagementIcon,
      activeIcon: ActiveEmpManagementIcon,
      feature: FEATURES.EMPLOYEE_MANAGEMENT,
    },
  ];

  // Filter menu items based on user role
  const userRole = user?.admin_role?.toUpperCase();
  
  const menuItems = allMenuItems.filter(item => 
    hasFeatureAccess(userRole, item.feature)
  );

  const handleMenuClick = (itemId) => {
    navigate(`/${itemId}`);
  };

  const isActiveRoute = (itemId) => {
    return (
      location.pathname === `/${itemId}` ||
      (itemId === "dashboard" && location.pathname === "/dashboard") ||
      (itemId === "products" &&
        (location.pathname.startsWith("/product/") ||
          location.pathname === "/brands" ||
          location.pathname === "/category" ||
          location.pathname === "/batches" ||
          location.pathname === "/purchases" ||
          location.pathname === "/low-stock")) ||
      (itemId === "customers" &&
        (location.pathname === "/customers" ||
          location.pathname.startsWith("/customer/") ||
          location.pathname === "/customers/print")) ||
      (itemId === "vendors" &&
        (location.pathname === "/vendors" ||
          location.pathname.startsWith("/vendor/"))) ||
      (itemId === "sales" &&
        (location.pathname === "/sales" ||
          location.pathname.startsWith("/sales/"))) ||
      (itemId === "finance" &&
        (location.pathname === "/finance" ||
          location.pathname.startsWith("/finance/"))) ||
      (itemId === "imei" &&
        (location.pathname === "/imei" ||
          location.pathname.startsWith("/imei/"))) ||
      // ✅ Orders Active Route
      (itemId === "orders" &&
        (location.pathname === "/orders" ||
          location.pathname.startsWith("/orders/"))) ||
      // ✅ Returns Active Route
      (itemId === "returns" &&
        (location.pathname === "/returns" ||
          location.pathname.startsWith("/returns/register") ||
          location.pathname.startsWith("/returns/repair-reserve") ||
          location.pathname.startsWith("/returns/vendor")))
    );
  };

  const getIconComponent = (icon, isActive, id, activeIcon) => {
    // ✅ If icon is a React component (like CiRedo), return it directly
    if (React.isValidElement(icon)) {
      return isActive
        ? activeIcon
        : icon;
    }

    // Otherwise, it’s an SVG path (fallback)
    let iconSrc = icon;
    let activeIconSrc = activeIcon;
    if (icon && typeof icon === "object" && icon.default) {
      iconSrc = icon.default;
      activeIconSrc = activeIcon.default;
    } else if (typeof icon === "string") {
      iconSrc = icon;
      activeIconSrc = activeIcon;
    }

    return (
      <img
        src={isActive ? activeIconSrc : iconSrc}
        alt="icon"
        className="nav-icon"
        style={{
          width: 20,
          height: 20,
          display: "block",
          objectFit: "contain",
        }}
      />
    );
  };

  return (
    <div
      className={`sidebar ${
        isCollapsed ? "sidebar-collapsed" : "sidebar-open"
      } ${isMobileMenuOpen ? "sidebar-mobile-open" : "sidebar-mobile-closed"}`}
    >
      <div className="sidebar-content">
        {/* Header */}
        <div
          className={`${
            isCollapsed ? "sidebar-header-collapsed" : "sidebar-header"
          }`}
        >
          <div className="sidebar-logo">
            <img src={isCollapsed ? ShortLogo : Logo} alt="Logo" />
            <button
              className={`${
                isCollapsed
                  ? "sidebar-toggle-btn-collapsed"
                  : "sidebar-toggle-btn-open"
              }`}
              onClick={toggleSidebar}
            >
              {isCollapsed ? (
                <ChevronRight size={25} />
              ) : (
                <ChevronLeft size={25} />
              )}
            </button>
          </div>
        </div>

        {/* Navigation Body */}
        <nav className="sidebar-nav no-scrollbar">
          <ul className="nav-list">
            {menuItems.map((item) => {
              const isActive = isActiveRoute(item.id);
              return (
                <li key={item.id} className="nav-item">
                  <button
                    className={`nav-link ${isActive ? "active" : ""}`}
                    onClick={() => handleMenuClick(item.id)}
                  >
                    <span className="nav-icon">
                      {getIconComponent(item.icon, isActive, item.id, item.activeIcon)}
                    </span>
                    {!isCollapsed && (
                      <span className="nav-text">{item.label}</span>
                    )}
                    <div className="nav-tooltip">{item.label}</div>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Settings */}
          {hasFeatureAccess(userRole, FEATURES.SETTINGS) && (
            <div className="sidebar-settings">
              <button
                className={`nav-link ${
                  isActiveRoute("settings") ? "active" : ""
                }`}
                onClick={() => handleMenuClick("settings")}
              >
                <Settings className="nav-icon" />
                {!isCollapsed && <span className="nav-text">Settings</span>}
                <div className="nav-tooltip">Settings</div>
              </button>
            </div>
          )}

          {/* Admin Info */}
          <div className="sidebar-admin-info">
            <div className="admin-profile">
              <div className="admin-avatar">
                {hasProfileImage(user) ? (
                  <img
                    src={getProfileImageUrl(user)}
                    alt="Profile"
                    className="avatar-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <span
                  className="avatar-text"
                  style={{ display: hasProfileImage(user) ? "none" : "flex" }}
                >
                  {getUserInitials(user)}
                </span>
              </div>
              {!isCollapsed && (
                <div className="admin-details">
                  <div className="admin-name">{getDisplayName(user)}</div>
                  <div className="admin-role">{userRole}</div>
                </div>
              )}
              <div className="admin-tooltip">
                <div className="tooltip-name">{getDisplayName(user)}</div>
                <div className="tooltip-email">
                  {user?.display_email || userRole}
                </div>
              </div>
            </div>
          </div>

          {/* Logout */}
          <div className="signout-container">
            <button className="signout-link" onClick={handleLogout}>
              <LogOut className="signout-icon" />
              {!isCollapsed && <span className="signout-text">Log Out</span>}
              <div className="tooltip">Log Out</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
